import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import styles from './AuthModal.module.css';

export default function AuthModal({ onClose }) {
  const { signIn, signUp, signInWithGoogle, resetPassword, isSupabaseEnabled } = useAuth();

  const [mode,     setMode]     = useState('login');   // 'login' | 'signup' | 'forgot'
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  const reset = () => { setError(''); setSuccess(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    reset();

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) throw error;
        onClose();

      } else if (mode === 'signup') {
        if (!email || !password) throw new Error('Email and password are required');
        if (password.length < 6) throw new Error('Password must be at least 6 characters');
        const { error } = await signUp(email, password, name);
        if (error) throw error;
        setSuccess('Account created! Check your email to confirm your account.');

      } else if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) throw error;
        setSuccess('Password reset email sent — check your inbox.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    reset();
    const { error } = await signInWithGoogle();
    if (error) { setError(error.message); setLoading(false); }
    // redirect happens automatically
  };

  if (!isSupabaseEnabled) {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={e => e.stopPropagation()}>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
          <div className={styles.header}>
            <div className={styles.logo}>Mio<span className={styles.accent}>Price</span></div>
          </div>
          <div className={styles.noticeBox}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Auth not configured</div>
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              Add <code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code> to your <code>.env</code> file to enable sign up and login.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>×</button>

        {/* Logo */}
        <div className={styles.header}>
          <div className={styles.logo}>Mio<span className={styles.accent}>Price</span></div>
          <div className={styles.tagline}>
            {mode === 'login'  && 'Welcome back'}
            {mode === 'signup' && 'Create your free account'}
            {mode === 'forgot' && 'Reset your password'}
          </div>
        </div>

        {/* Google sign in */}
        {mode !== 'forgot' && (
          <button className={styles.googleBtn} onClick={handleGoogle} disabled={loading}>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        )}

        {mode !== 'forgot' && <div className={styles.divider}><span>or</span></div>}

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>
          {mode === 'signup' && (
            <div className={styles.field}>
              <label>Name <span className={styles.optional}>(optional)</span></label>
              <input type="text" placeholder="Your name" value={name}
                onChange={e => setName(e.target.value)} disabled={loading} />
            </div>
          )}

          <div className={styles.field}>
            <label>Email</label>
            <input type="email" placeholder="you@example.com" value={email}
              onChange={e => setEmail(e.target.value)} required disabled={loading} />
          </div>

          {mode !== 'forgot' && (
            <div className={styles.field}>
              <label>Password</label>
              <input type="password" placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'}
                value={password} onChange={e => setPassword(e.target.value)}
                required disabled={loading} />
            </div>
          )}

          {error   && <div className={styles.error}>⚠ {error}</div>}
          {success && <div className={styles.success}>✓ {success}</div>}

          <button type="submit" className={styles.submitBtn} disabled={loading}>
            {loading ? 'Please wait…' :
              mode === 'login'  ? 'Sign in' :
              mode === 'signup' ? 'Create account' : 'Send reset email'}
          </button>
        </form>

        {/* Mode switchers */}
        <div className={styles.footer}>
          {mode === 'login' && <>
            <span>Don't have an account?</span>
            <button className={styles.linkBtn} onClick={() => { setMode('signup'); reset(); }}>Sign up free</button>
          </>}
          {mode === 'login' && <>
            <span style={{ margin: '0 6px', color: '#cbd5e1' }}>·</span>
            <button className={styles.linkBtn} onClick={() => { setMode('forgot'); reset(); }}>Forgot password?</button>
          </>}
          {mode === 'signup' && <>
            <span>Already have an account?</span>
            <button className={styles.linkBtn} onClick={() => { setMode('login'); reset(); }}>Sign in</button>
          </>}
          {mode === 'forgot' && <>
            <button className={styles.linkBtn} onClick={() => { setMode('login'); reset(); }}>Back to sign in</button>
          </>}
        </div>

      </div>
    </div>
  );
}
